import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import StepsLayout from '../utilities/layouts/StepsLayout';
import { Box, Paper } from '@material-ui/core/';
import { makeStyles } from '@material-ui/core/styles';
import { styleBreakDesktop, styleBreakMobile, styleBreakSmallMobile } from '../utilities/layouts/Globals';
import { HealthCardSystem } from '../DropDownSelector';
import { ErrorTypography } from '../utilities/commonUtilities/Typography';
import DataSubmissionHandler from '../DataSubmissionHandler';
import { NedVerifyAccountLayout, WatchmanVerifyAccountLayout, VerifyAccountSummary } from '../utilities/layouts/SetupAccountLayout';
import { connect } from 'react-redux';
import { ACCOUNT_SETUP_SUBMISSION_ERROR_MESSAGE } from '../../resources/messages.json';

const useStyles = makeStyles(theme => ({
    container: {
        marginBottom: theme.spacing(6),
        [styleBreakDesktop(theme)]: {
            width: theme.spacing(102),
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(4.75),
            paddingLeft: theme.spacing(4),
            paddingRight: theme.spacing(4),
            //negative margins to offset margins set from marginSelector in StepsLayout.js
            marginLeft: theme.spacing(-2),
            marginRight: theme.spacing(-2),
            marginBottom: theme.spacing(12),
        },
        [styleBreakMobile(theme)]: {
            height: '100%',
            paddingTop: theme.spacing(3),
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
            //negative margins to offset margins set from marginSelector in StepsLayout.js
            marginLeft: theme.spacing(-3),
            marginRight: theme.spacing(-3),
        },
        [styleBreakSmallMobile(theme)]: {
            height: '100%',
            paddingTop: theme.spacing(2),
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
        },
    },
}));

export const ErrorContainer = ({ shouldShow, children, cyClassName }) => {
    return (
        <Box mt={0.5} style={{ display: shouldShow ? 'block' : 'none' }} data-cy={!!cyClassName ? cyClassName : 'errorContainer'}>
            {children}
        </Box>
    );
};

export const VerificationErrorCodes = {
    USER_ALREADY_ENABLED: 'USER_ALREADY_ENABLED',
    EXPIRED_ACTIVATION_CODE: 'EXPIRED_ACTIVATION_CODE',
    ACTIVATION_CODE_NOT_FOUND: 'ACTIVATION_CODE_NOT_FOUND',
    MAXIMUM_ATTEMPTS_EXCEEDED: 'MAXIMUM_ATTEMPTS_EXCEEDED',
    CHALLENGE_FAILED: 'CHALLENGE_FAILED',
};

const SetupAccountStep3Verify = ({
    currentPage,
    onNextPage,
    onPreviousPage,
    healthCardNumber,
    healthCardSystem,
    dateOfBirth,
    verifyPatientInformation,
    verificationResponse,
    onCancel,
    sessionToken,
    isWatchman,
    accountSetupVerificationParameters,
}) => {
    const style = useStyles();
    const formRefBag = useRef();
    let newYear = '';
    let newMonth = '';
    let newDate = '';
    if (!!dateOfBirth) {
        let dob = moment(dateOfBirth);
        newYear = dob.year();
        newMonth = dob.month();
        newDate = dob.format('DD');
    }

    let newHealthCardNumber = healthCardNumber;
    let newHealthCardSystem = isWatchman ? HealthCardSystem.ONTARIO : healthCardSystem;
    let [hasPageAdvanced, setHasPageAdvanced] = useState(false);

    useEffect(() => {
        // The store will update with the token.  If we have the token and this page has not yet been automatically advanced, just go to the next page.
        // Otherwise, do nothing.
        if (!!verificationResponse && !!verificationResponse.token && !hasPageAdvanced) {
            onNextPage();
            setHasPageAdvanced(true);
        }
    }, [verificationResponse, onNextPage, hasPageAdvanced, setHasPageAdvanced]);

    const [isButtonEnabled, setButtonEnabled] = useState(false);
    const [submittingGlobal, setSubmittingGlobal] = useState(false);
    // creates an action that's dispatch when form is submitted
    const [submissionActionCreator, setSubmissionActionCreator] = useState(null);

    // Don't draw the component if it is not our page.
    if (currentPage !== 3) {
        return null;
    }

    // Calculate some state.
    const isVerificationAlreadySuccessful = !!(verificationResponse?.token ? verificationResponse.token : sessionToken);
    const isMaximumAttemptsExceeded = verificationResponse?.error === VerificationErrorCodes.MAXIMUM_ATTEMPTS_EXCEEDED;
    const isExpiredActivationCode =
        verificationResponse?.error === VerificationErrorCodes.EXPIRED_ACTIVATION_CODE ||
        verificationResponse?.error === VerificationErrorCodes.ACTIVATION_CODE_NOT_FOUND;
    const isIncorrectInput = verificationResponse?.error === VerificationErrorCodes.CHALLENGE_FAILED;
    const isOtherError =
        verificationResponse?.status && verificationResponse.status !== 400 && verificationResponse.status !== 200 && verificationResponse.status !== 403;

    // Using Yup here for validation as it is built into Formik.
    const IdentitySchema = Yup.object().shape({
        year: Yup.number()
            .required('Required')
            .min(1900, 'Invalid Year')
            .max(moment().year(), 'Invalid Year'),
        month: Yup.string().required('Month is required'),
        date: Yup.number()
            .required('Required')
            .min(1, 'Invalid Date')
            .max(31, 'Invalid Date'),
        healthCardNumber: Yup.string().required('Health card number is required'),
    });

    const healthCardPatternMatch = (healthCardSystem, healthCardNumber) => {
        if (healthCardSystem === HealthCardSystem.ONTARIO) {
            return /^\d{4}-\d{3}-\d{3}$/.test(healthCardNumber);
        } else if (healthCardSystem === HealthCardSystem.ALBERTA) {
            return /^\d{5}-\d{4}$/.test(healthCardNumber);
        } else if (healthCardSystem === HealthCardSystem.NOVA_SCOTIA) {
            return /^\d{4} \d{3} \d{3}$/.test(healthCardNumber);
        } else {
            return false;
        }
    };

    const accountVerifySubmissionFailureTitle = 'Submission Error';
    const accountVerifySubmissionFailureBody = ACCOUNT_SETUP_SUBMISSION_ERROR_MESSAGE;

    const defaultOnNextClick = () => {
        if (isVerificationAlreadySuccessful) {
            onNextPage();
        }
    };

    return (
        <>
            <StepsLayout
                onPrevClick={null}
                currentCount={2}
                onCancelClick={onCancel}
                titleText={'Account Setup'}
                nextButtonText={'Next'}
                isNextButtonDisabled={isButtonEnabled}
                id={'Accept-button-of-terms-service'}
                onNextClick={formRefBag.current?.submitForm || defaultOnNextClick}
                totalCount={4}
                submitting={submittingGlobal}
                submittingText={'Verifying'}
            >
                <Paper elevation={0} className={style.container}>
                    <VerifyAccountSummary isWatchman={isWatchman} />
                    <Box mt={4}>
                        <Formik
                            innerRef={formRefBag}
                            initialValues={{
                                year: newYear,
                                month: newMonth,
                                date: newDate,
                                healthCardNumber: newHealthCardNumber,
                                healthCardSystem: newHealthCardSystem,
                            }}
                            validationSchema={IdentitySchema}
                            onSubmit={(values, { setSubmitting }) => {
                                if (!isVerificationAlreadySuccessful) {
                                    // Save the state of our page here upon submit.
                                    let newDob = moment()
                                        .year(values.year)
                                        .month(values.month)
                                        .date(values.date)
                                        .hour(0)
                                        .minute(0)
                                        .second(0)
                                        .millisecond(0);
                                    setSubmissionActionCreator(prevFunc => dispatchDataSubmissionResult => {
                                        return verifyPatientInformation(
                                            accountSetupVerificationParameters?.accountSetupToken,
                                            values.healthCardSystem,
                                            values.healthCardNumber,
                                            newDob.toISOString(),
                                            dispatchDataSubmissionResult
                                        );
                                    });
                                    setSubmittingGlobal(true); // indicates that data is being sent to backend
                                    setSubmitting(false);
                                } else {
                                    // The verification has already been submitted.  We are probably in this code because the user had come back to this page.  Just go to the next page.
                                    setSubmitting(false);
                                    onNextPage();
                                }
                            }}
                        >
                            {({ errors, touched, isSubmitting, isValid, dirty, values }) => {
                                //Work around for warning:
                                //Cannot update a component (`SetupAccountStep3Verify`) while rendering a different component (`Formik`)
                                //https://github.com/formium/formik/issues/1218
                                setTimeout(
                                    () =>
                                        setButtonEnabled(
                                            !isVerificationAlreadySuccessful &&
                                                (isSubmitting ||
                                                    !isValid ||
                                                    !dirty ||
                                                    !healthCardPatternMatch(values.healthCardSystem, values.healthCardNumber))
                                        ),
                                    0
                                );

                                return (
                                    <>
                                        {isWatchman ? (
                                            <WatchmanVerifyAccountLayout
                                                isVerificationAlreadySuccessful={isVerificationAlreadySuccessful}
                                                values={values}
                                                touched={touched}
                                                errors={errors}
                                                healthCardPatternMatch={healthCardPatternMatch}
                                            />
                                        ) : (
                                            <NedVerifyAccountLayout
                                                isVerificationAlreadySuccessful={isVerificationAlreadySuccessful}
                                                values={values}
                                                touched={touched}
                                                errors={errors}
                                                healthCardPatternMatch={healthCardPatternMatch}
                                            />
                                        )}

                                        <ErrorContainer shouldShow={isExpiredActivationCode} cyClassName="error_account_setup_activation_code_is_expired">
                                            <ErrorTypography variant="caption" color="error">
                                                Your token has expired. Please contact the coordinator for a new verification link.
                                            </ErrorTypography>
                                        </ErrorContainer>
                                        <ErrorContainer
                                            shouldShow={!accountSetupVerificationParameters?.accountSetupToken}
                                            cyClassName="error_account_setup_no_token"
                                        >
                                            <ErrorTypography variant="caption" color="error">
                                                Please try again using the link provided in your email to verify your identity.
                                            </ErrorTypography>
                                        </ErrorContainer>
                                        <ErrorContainer shouldShow={isMaximumAttemptsExceeded} cyClassName="error_account_setup_maximum_attempts">
                                            <ErrorTypography variant="caption" color="error">
                                                Your account has been locked because you have reached the maximum number of attempts. To unlock your account,
                                                please contact the administrator
                                            </ErrorTypography>
                                        </ErrorContainer>
                                        <ErrorContainer shouldShow={isOtherError} cyClassName="error_account_setup_other_error">
                                            <ErrorTypography variant="caption" color="error">
                                                Error validating identity:{' '}
                                                {verificationResponse?.error ? verificationResponse.error : verificationResponse?.status}
                                            </ErrorTypography>
                                        </ErrorContainer>
                                        <ErrorContainer shouldShow={isIncorrectInput} cyClassName="error_account_setup_incorrect_information">
                                            <ErrorTypography variant="caption" color="error">
                                                Your date of birth or health card number is incorrect.
                                            </ErrorTypography>
                                            <ErrorTypography variant="caption" color="error">
                                                Please try again.
                                            </ErrorTypography>
                                        </ErrorContainer>
                                    </>
                                );
                            }}
                        </Formik>
                    </Box>
                </Paper>
            </StepsLayout>
            <DataSubmissionHandler
                setSubmitting={setSubmittingGlobal}
                submitting={submittingGlobal}
                actionCreator={submissionActionCreator}
                failureMsgTitle={accountVerifySubmissionFailureTitle}
                failureMsgBody={accountVerifySubmissionFailureBody}
            />
        </>
    );
};

/* type checking for props */
SetupAccountStep3Verify.propTypes = {
    currentPage: PropTypes.number.isRequired,
    onNextPage: PropTypes.func.isRequired,
    onPreviousPage: PropTypes.func.isRequired,
    healthCardNumber: PropTypes.string.isRequired,
    healthCardSystem: PropTypes.string.isRequired,
    dateOfBirth: PropTypes.string.isRequired,
    verifyPatientInformation: PropTypes.func.isRequired,
    verificationResponse: PropTypes.object.isRequired,
};

const mapStateToProps = state => {
    return {
        accountSetupVerificationParameters: state.accountSetup,
    };
};

export default connect(mapStateToProps, null)(SetupAccountStep3Verify);
